import { inject, Injectable } from '@angular/core';
import { TransactionsDataProviderService } from '@app/_shared/data/transactions/transactions-data-provider.service';
import {
  AppDataReloadService,
  RULE_FIELDS,
  TRANSACTION_FIELDS,
} from '@app/_store/services/app-data-reload.service';
import { FeaturesHttpEndpoints } from '@core/enums/features-http-endpoints';
import { errorCodesAsData } from '@core/interceptors/error.interceptor';
import { DefaultResponse } from '@core/models/interfaces/http/default-response';
import { BulkEditResponse } from '@core/models/interfaces/storage-data/bulk-edit';
import {
  isRuleConflictResponse,
  RULE_CONFLICT_ERROR_CODE,
  SaveRulePayload,
  SaveRuleResponse,
} from '@core/models/interfaces/storage-data/rules';
import { DataProviderService } from '@core/services/data-provider/data-provider.service';
import { Observable, of, switchMap } from 'rxjs';

/**
 * A8. The Rules section, moved with the Transactions section it is coupled to:
 * `getRuleSaveStatus` polls through the transactions provider's `getBulkEditStatus`, which is
 * why the audit's plan ships them in one batch. Bodies are byte-for-byte the originals.
 */
@Injectable({ providedIn: 'root' })
export class RulesDataProviderService extends DataProviderService {
  private readonly appReload = inject(AppDataReloadService);
  private readonly transactionsData = inject(TransactionsDataProviderService);

  /**
   * Creates or edits a transaction rule.
   *
   * A conflict with existing rules is **not** an exception here: the server
   * answers `status: 'error'` with `RULE_CONFLICT_ERROR_CODE`. This request asks
   * the interceptor to pass that one code through untoasted, so it arrives as a
   * normal value; every other error envelope is raised as an ApiError. Callers
   * have to check `isRuleConflictResponse` before reading `data.editStatus`.
   */
  public saveRule(payload: SaveRulePayload): Observable<SaveRuleResponse> {
    return this.create<SaveRulePayload, SaveRuleResponse>(
      FeaturesHttpEndpoints.RULES_SAVE,
      payload,
      undefined,
      { context: errorCodesAsData(RULE_CONFLICT_ERROR_CODE) }
    ).pipe(
      // Same shape as bulk edit (R116): the reload is only worth waiting for once
      // the server says the edit finished. An unfinished edit is polled instead.
      switchMap((response) =>
        !isRuleConflictResponse(response) &&
        // Measured on the dev API 2026-08-13: with `applyToExisting: false` the
        // endpoint creates the rule and answers `{status: 'success', data: null}` —
        // there is no edit to poll, so there is no session to report. That reply
        // still needs the reload, and reading `editStatus` off null used to throw.
        (response.data == null || response.data.editStatus === 'done')
          ? // Measured 2026-08-13: a delta `sync/fetchData` does not bring a rule the
            // client has just created — the container came back without it, and only a
            // reset (`syncAnchor: 0`) returned it. So the rules container is reloaded
            // with the anchor reset; it is small, and asking for it alone keeps the
            // transactions on their delta. Drop the reset once the server's delta
            // reports new rules.
            this.appReload.reloadAndWaitFor(response, { rules: {} }, true).pipe(
              switchMap((saved) =>
                // A rule applied to existing transactions changed them too, so what a
                // bulk edit invalidates still has to come back — on its own anchors.
                payload.applyToExisting
                  ? this.appReload.reloadAndWaitFor(saved, TRANSACTION_FIELDS)
                  : of(saved)
              )
            )
          : of(response)
      )
    );
  }

  /** Polls a rule save; differs from a bulk edit only in what it reloads. */
  public getRuleSaveStatus(
    editSessionId: string
  ): Observable<BulkEditResponse> {
    return this.transactionsData.getBulkEditStatus(editSessionId, RULE_FIELDS);
  }

  public deleteRule(id: string | number): Observable<DefaultResponse> {
    return this.create<{ id: string | number }, DefaultResponse>(
      FeaturesHttpEndpoints.RULES_DELETE,
      { id }
    ).pipe(
      // Measured 2026-08-13: the delete succeeds, and the delta `sync/fetchData` that
      // followed came back empty (`data: {}`) — the deleted rule stayed on screen. The
      // anchor is reset for the same reason a save resets it.
      this.appReload.reloadAndWait({ rules: {} }, true)
    );
  }

  /**
   * Marks a rule as seen, which is what clears its red indicator. The rules
   * container is reloaded so the flag the server now holds is the one the UI
   * reads.
   */
  public acknowledgeRule(id: string | number): Observable<DefaultResponse> {
    return this.create<{ id: string | number }, DefaultResponse>(
      FeaturesHttpEndpoints.RULES_ACKNOWLEDGE,
      { id }
    ).pipe(
      // Measured 2026-08-13 on an account of 14 migrated rules: the acknowledge
      // succeeds and the delta `sync/fetchData` comes back empty, so the red dot never
      // went away. Third call with the same anchor behaviour, reset like the others.
      this.appReload.reloadAndWait({ rules: {} }, true)
    );
  }
}
